import React from 'react';
import type { EstadoTienda } from '../../types/metas.types';
import { cx } from './clases';

type ClavePaso = 'captura' | 'revision' | 'autorizacion' | 'autorizada';
type Paso = { clave: ClavePaso; titulo: string };

/**
 * Los cuatro pasos del flujo con la captura de la TIENDA encendida (21-oct-2026,
 * decisión del usuario, fase B: el gerente captura, el supervisor revisa y el
 * regional autoriza). «Rechazada» no es un paso: se señala en el de la
 * autorización del regional.
 */
export const PASOS_CON_TIENDA: Paso[] = [
  { clave: 'captura', titulo: 'Captura del gerente' },
  { clave: 'revision', titulo: 'Revisión del supervisor' },
  { clave: 'autorizacion', titulo: 'Autorización del regional' },
  { clave: 'autorizada', titulo: 'Autorizada' },
];

/**
 * Los tres pasos del flujo de dos manos, el de antes (22-oct-2026, decisión del
 * usuario: «de momento seguir dejando la tarea de capturar la meta a los
 * supervisores»). Es el que corre mientras `capturaTienda` esté apagado.
 */
export const PASOS_SUPERVISOR: Paso[] = [
  { clave: 'captura', titulo: 'Captura del supervisor' },
  { clave: 'autorizacion', titulo: 'Autorización del regional' },
  { clave: 'autorizada', titulo: 'Autorizada' },
];

/** Los pasos según el interruptor del entorno (`capturaDeTienda`); por omisión, sin la tienda. */
export const pasosDelFlujo = (capturaTienda = false): Paso[] => (capturaTienda ? PASOS_CON_TIENDA : PASOS_SUPERVISOR);

type Situacion = 'hecho' | 'actual' | 'rechazado' | 'pendiente';

/** En qué paso va cada estado (índice de `PASOS_CON_TIENDA`). */
const PASO_DE: Record<EstadoTienda, number> = {
  sin_captura: 0,
  borrador: 0,
  revision: 1,
  enviada: 2,
  rechazada: 2,
  autorizada: 3,
};

/**
 * Lo mismo en `PASOS_SUPERVISOR`. `revision` (una que quedó «con el
 * supervisor» de cuando la tienda capturaba) sigue en su captura: la puede
 * ajustar y enviar.
 */
const PASO_DE_SUPERVISOR: Record<EstadoTienda, number> = {
  sin_captura: 0,
  borrador: 0,
  revision: 0,
  enviada: 1,
  rechazada: 1,
  autorizada: 2,
};

/**
 * Qué le toca a cada paso según el estado de la tienda.
 * Con la tienda (`capturaTienda`):
 *   sin_captura y borrador → paso 1 actual (la captura; la del gerente o, si él
 *     no la manda, la del supervisor) · revision → 1 hecho, 2 actual
 *   enviada → 1-2 hechos, 3 actual · autorizada → todos hechos
 *   rechazada → 1-2 hechos, 3 RECHAZADO (el regional la devolvió al supervisor)
 * Sin ella (por omisión, 22-oct-2026):
 *   sin_captura, borrador y revision → 1 actual · enviada → 1 hecho, 2 actual
 *   rechazada → 1 hecho, 2 RECHAZADO · autorizada → todos hechos
 */
export function situacionPasos(estado: EstadoTienda | string, capturaTienda = false): Situacion[] {
  const rechazada = estado === 'rechazada';
  const i = (capturaTienda ? PASO_DE : PASO_DE_SUPERVISOR)[estado as EstadoTienda] ?? 0;
  return pasosDelFlujo(capturaTienda).map((_, k) => {
    if (k < i) return 'hecho';
    if (k > i) return 'pendiente';
    if (rechazada) return 'rechazado';
    return estado === 'autorizada' ? 'hecho' : 'actual';
  });
}

interface Props {
  estado: EstadoTienda | string;
  /** Renglón pequeño bajo el paso actual («Guardado hoy 10:42», «2º envío»). */
  detalle?: React.ReactNode;
  /** Sólo el paso actual con su número («Paso 2 de 4 · Revisión del supervisor»), para el teléfono. */
  compacto?: boolean;
  className?: string;
  /**
   * La tienda captura (el gerente, fase B): cuatro pasos. Por omisión, apagado
   * (22-oct-2026, decisión del usuario): los tres del supervisor.
   */
  capturaTienda?: boolean;
}

const Palomita = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12l5 5 9-10" />
  </svg>
);

const Tache = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const SR_SITUACION: Record<Situacion, string> = {
  hecho: 'completado',
  actual: 'paso actual',
  rechazado: 'rechazada por el regional',
  pendiente: 'pendiente',
};

/**
 * Captura del supervisor → Autorización del regional → Autorizada (el flujo de
 * siempre, el de producción desde el 22-oct-2026) o, con la captura de la
 * tienda encendida, Captura del gerente → Revisión del supervisor →
 * Autorización del regional → Autorizada (21-oct-2026), con el paso actual marcado
 * (`aria-current="step"`) y la rechazada señalada en rojo con un tache y su
 * nombre. El estado nunca va sólo por color: cada paso dice en texto oculto
 * si está completado, es el actual o está pendiente.
 */
export const PasosEstado: React.FC<Props> = ({ estado, detalle, compacto, className, capturaTienda = false }) => {
  const pasos = pasosDelFlujo(capturaTienda);
  const sit = situacionPasos(estado, capturaTienda);
  const iActual = sit.findIndex((s) => s === 'actual' || s === 'rechazado');
  const actual = iActual >= 0 ? iActual : pasos.length - 1;

  if (compacto) {
    const rechazada = sit[actual] === 'rechazado';
    return (
      <p className={cx('m-0 text-[13px] text-metas-texto-2', className)} data-testid="pasos-estado">
        Paso {actual + 1} de {pasos.length} ·{' '}
        <strong className={cx('font-semibold', rechazada ? 'text-metas-error' : 'text-metas-texto')}>
          {rechazada ? 'Rechazada' : pasos[actual].titulo}
        </strong>
        {detalle && <span> · {detalle}</span>}
      </p>
    );
  }

  return (
    <ol
      aria-label="Estado de la captura"
      className={cx('m-0 flex list-none flex-wrap items-center gap-3 p-0', className)}
      data-testid="pasos-estado"
    >
      {pasos.map((p, k) => {
        const s = sit[k];
        const esActual = k === actual;
        const titulo = s === 'rechazado' ? 'Rechazada' : p.titulo;
        return (
          <React.Fragment key={p.clave}>
            {k > 0 && (
              <li
                aria-hidden="true"
                className={cx('h-0.5 w-7', sit[k - 1] === 'hecho' ? 'bg-metas-exito' : 'bg-metas-conector')}
              />
            )}
            <li
              aria-current={esActual ? 'step' : undefined}
              data-situacion={s}
              className={cx(
                'flex items-center gap-2 whitespace-nowrap text-[13px]',
                esActual ? 'font-semibold text-metas-texto' : 'text-metas-texto-2',
                s === 'rechazado' && 'text-metas-error',
              )}
            >
              <span
                aria-hidden="true"
                className={cx(
                  'flex h-6 w-6 items-center justify-center rounded-full border-[1.5px] text-xs font-semibold',
                  s === 'hecho' && 'border-metas-exito bg-metas-exito text-white',
                  s === 'actual' && 'border-metas-primario bg-metas-panel text-metas-primario',
                  s === 'rechazado' && 'border-metas-error bg-metas-error text-white',
                  s === 'pendiente' && 'border-metas-paso bg-metas-panel',
                )}
              >
                {s === 'hecho' ? <Palomita /> : s === 'rechazado' ? <Tache /> : k + 1}
              </span>
              <span className="flex flex-col gap-px">
                <span>
                  {titulo}
                  <span className="sr-only"> ({SR_SITUACION[s]})</span>
                </span>
                {esActual && detalle && <span className="text-[11px] font-normal text-metas-texto-2">{detalle}</span>}
              </span>
            </li>
          </React.Fragment>
        );
      })}
    </ol>
  );
};

export default PasosEstado;
